import { QueryResult, ApolloProvider, useQuery } from '@apollo/client'
import { Router, Redirect, LocationProvider } from '@reach/router'
import * as Sentry from '@sentry/browser'
import * as React from 'react'
import { render } from 'react-dom'
import styled from 'styled-components'
import { Colors, DarkColors } from '../../../common/src/constants'
import { appContext } from '../../../common/src/context'
import title from '../../../common/src/title'
import { FetchAppContextQuery, FetchUserContextQuery } from '../../../common/src/types.gen'
import { apolloWebClient } from '../apolloClientForWeb'
import { fetchAppContextQuery } from '../graphql/queries/AppContextQuery'
import { fetchUserContextQuery } from '../graphql/queries/InboxQuery'
import FirehosePage from './FirehosePage'
import LoginPage from './LoginPage'
import StatusPage from './StatusPage'
import AdminPage from './admin/AdminPage'
import { AppContext, AppCtx } from './app-context'
import BulkUpdatePage from './bulk-update/BulkUpdatePage'
import Analytics from './components/Analytics'
import ComponentsPage from './components/ComponentsPage'
import { PollingQuery } from './components/PollingQuery'
import Header, { HEADER_HEIGHT } from './header/Header'
import InboxPage, { TasksPage } from './inbox/InboxPage'
import LobbyPage from './lobby/LobbyPage'
import ReleaseNotesPage from './release-notes/ReleaseNotesPage'
import { Route } from './route'

/**
 * Main entry point for Command Center.
 *
 * Component structure:
 *
 *
 *    +--------------------+     +-------------------+
 *    | LoginPage          |     | ApiExplorerPage   |
 *    |                    |     |                   |
 *    |                    |     |                   |
 *    |                    |     |                   |
 *    |                    |     |                   |
 *    |                    |     |                   |
 *    |                    |     |                   |
 *    +--------------------+     +-------------------+
 *
 *
 *    +----------------------------------------------+
 *    | Header                                       |
 *    +----------------------------------------------+
 *    | Page:                                        |
 *    | LobbyPage                                    |
 *    | InboxPage                                    |
 *    | AdminPage                                    |
 *    |                                              |
 *    |                                              |
 *    |                                              |
 *    +----------------------------------------------+
 *
 *
 *    +----------------------------------------------+
 *    | Header                                       |
 *    +--------------+--------------+----------------+
 *    | InboxPage                                    |
 *    | InboxSidebar | TaskDetail, ThreadDetail      |
 *    |              | ...          | ContextSidebar |
 *    |              |              |                |
 *    |              |              |                |
 *    |              |              |                |
 *    |              |              |                |
 *    +--------------+--------------+----------------+
 *
 *
 */
export function init() {
  const container = document.getElementById('App-container')
  render(
    <ApolloProvider client={apolloWebClient}>
      <App />
    </ApolloProvider>,
    container
  )
}

/**
 * Polls GraphQL.
 *
 * Loads the rest of the app with resulting context: whether we're logged in, unread threads, etc.
 */
function App() {
  const { data } = useQuery<FetchAppContextQuery>(fetchAppContextQuery)

  return (
    <PollingQuery<FetchUserContextQuery> query={fetchUserContextQuery}>
      {res => (
        <AppContext.Provider value={new AppCtx(data)}>
          <AppImpl res={res} />
        </AppContext.Provider>
      )}
    </PollingQuery>
  )
}

interface AppImplProps {
  res: QueryResult<FetchUserContextQuery>
}

class AppImpl extends React.PureComponent<AppImplProps> {
  constructor(props: AppImplProps) {
    super(props)
    this.state = {}
  }

  render() {
    const { res } = this.props
    const { data, error } = res
    if (error && error.message.includes('status code 401')) {
      return <LoginPage />
    }
    if (data == null || data.self == null) {
      return null
    }
    const user = data.self
    const canViewAdminPage = user.permissions.canViewAdminPage
    const canViewFirmSecrets = user.permissions.canViewFirmSecrets

    const { isProduction, commitHash } = appContext()
    const devCommitHash = isProduction ? undefined : commitHash
    return (
      <Page>
        <LocationProvider>
          <Analytics user={user} isProduction={isProduction || false} />
          <Header user={user} commitHash={devCommitHash} />
        </LocationProvider>
        <PageBody>
          <StyledRouter>
            <Redirect noThrow from="leasing" to="lobby" />
            <LobbyPage path={Route.LOBBY} user={user} />
            <InboxPage path={Route.INBOX} user={user} />
            <InboxPage path={Route.INBOX_THREAD} user={user} />
            <InboxPage path={Route.INBOX_THREAD_CONTACT} user={user} />
            <TasksPage path={Route.TASKS} user={user} />
            <TasksPage path={Route.TASK} user={user} />
            <TasksPage path={Route.TASK_THREAD} user={user} />
            <TasksPage path={Route.TASK_THREAD_CONTACT} user={user} />
            <TasksPage path={Route.NEW_TASK} user={user} tab="NEW_TASK" />
            <AdminPage path={Route.ADMIN_ENGINEERING} canViewAdminPage={canViewAdminPage} tab="ENGINEERING" />
            <AdminPage path={Route.ADMIN_ENGINEER} canViewAdminPage={canViewAdminPage} tab="ENGINEERING" />
            <AdminPage path={Route.ADMIN_OPERATIONS} canViewAdminPage={canViewAdminPage} tab="OPERATIONS" />
            <AdminPage path={Route.ADMIN_OPERATIONS_USER} canViewAdminPage={canViewAdminPage} tab="OPERATIONS" />
            <AdminPage path={Route.ADMIN_SUPPORT} canViewAdminPage={canViewAdminPage} tab="SUPPORT" />
            <AdminPage path={Route.ADMIN_SUPPORT_USER} canViewAdminPage={canViewAdminPage} tab="SUPPORT" />
            <AdminPage path={Route.ADMIN_OTHERS} canViewAdminPage={canViewAdminPage} tab="OTHER_USERS" />
            <AdminPage path={Route.ADMIN_OTHER} canViewAdminPage={canViewAdminPage} tab="OTHER_USERS" />
            <AdminPage
              path={Route.ADMIN_FIRMS}
              canViewAdminPage={canViewAdminPage}
              tab="FIRMS"
              canViewFirmSecrets={canViewFirmSecrets}
            />
            <AdminPage
              path={Route.ADMIN_FIRM}
              canViewAdminPage={canViewAdminPage}
              tab="FIRMS"
              canViewFirmSecrets={canViewFirmSecrets}
            />
            <AdminPage path={Route.ADMIN_NEW_FIRM} canViewAdminPage={canViewAdminPage} tab="NEW_FIRM" />
            <AdminPage path={Route.ADMIN_SETTINGS} canViewAdminPage={canViewAdminPage} tab="SETTINGS" />
            <BulkUpdatePage path={Route.BULK_UPDATE} user={user} />
            <StatusPage path={Route.STATUS} />
            <FirehosePage path={Route.FIREHOSE} />
            <ComponentsPage path={Route.COMPONENTS} />
            <ReleaseNotesPage path={Route.RELEASE_NOTES} />
          </StyledRouter>
        </PageBody>
      </Page>
    )
  }

  componentDidUpdate(pp: AppImplProps) {
    const prevR = pp.res
    const nextR = this.props.res
    if (nextR.data != null && nextR.data.self != null && (prevR.data == null || prevR.data.self == null)) {
      const user = nextR.data.self
      const ix = user.name.indexOf(' ')
      const firstName = ix < 0 ? user.name : user.name.substring(0, ix)
      const lastName = ix < 0 ? null : user.name.substring(ix + 1)
      Sentry.setUser({ first_name: firstName, last_name: lastName, id: user.id.toString() })
    }
  }

  static getDerivedStateFromProps(props: AppImplProps) {
    const { data } = props.res
    if (data == null || data.self == null) {
      return {}
    }

    // The unarchivedThreads GraphQL field always returns unarchived threads
    // However! When we archive a thread, that UpdateThreadMutation will update the Apollo cache.
    // This causes App to rerender with unarchivedThreads now containing an archived thread.
    const threads = data.self.unarchivedThreads.filter(t => !t.isArchived)

    maybeNotify(threads)

    return {}
  }
}

/**
 * Notifies the user when a new message comes in.
 *
 * Plays a sound, flashes the title bar.
 */
function maybeNotify(threads: FetchUserContextQuery['self']['unarchivedThreads']) {
  const hasNewMessage = !threads.every(t => t.lastReadMessageId >= t.lastInboundMessageId)

  if (hasNewMessage) {
    maybePlaySound()
  }

  // Flash the title no matter what, as long as unread count is > 0
  title.setFlashAltTitle(hasNewMessage ? 'New message' : null)
}

function maybePlaySound() {
  // Play sound only if the tab's been loaded for at least 5 seconds and unread count goes from 0 to 1.
  // Otherwise, you get noise every time you open a new CC tab while there are unread messages.
  const minSecsSincePageLoad = 5
  const isTabOldEnough = window.performance.now() >= minSecsSincePageLoad * 1000
  if (isTabOldEnough && !title.isFlashing()) {
    console.log('Playing new message sound')
    const audio = window.document.getElementById('audio-notification') as HTMLAudioElement
    if (audio != null) {
      // Ignore "play failed because user didn't interact with the document first"
      audio.play().catch(err => console.error(err))
    }
  }
}

/**
 * In addition to unarchivedThreads, user might be looking at a thread of their choice that is not
 * in unarchivedThreads. We want to trigger notification when this thread has new message if the
 * title is not flashing.
 */
export function maybeNotifyUpdate(thread: FetchUserContextQuery['self']['unarchivedThreads'][0]) {
  const hasNewMessage = thread.lastReadMessageId < thread.lastInboundMessageId
  if (hasNewMessage) {
    maybePlaySound()
    title.setFlashAltTitle('New message')
  }
}

const Page = styled.div`
  min-width: 1300px;
  scrollbar-color: ${Colors.midway} ${Colors.charcoal};
`

const PageBody = styled.div`
  position: absolute;
  top: ${HEADER_HEIGHT}px;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: ${Colors.ink};
  & ::selection {
    background-color: ${DarkColors.sky};
    color: ${Colors.white};
  }
`

const StyledRouter = styled(Router)`
  height: calc(100vh - ${HEADER_HEIGHT}px);
  max-height: calc(100vh - ${HEADER_HEIGHT}px);
`
